import React, { useState, useMemo } from "react";

const COLORS = {
  surface: "rgba(255,255,255,0.025)",
  border: "rgba(255,255,255,0.07)",
  indigo: "#6366F1",
  violet: "#7C3AED",
  text: "#F8F7FF",
  muted: "rgba(148,163,184,0.7)",
  green: "#10B981",
  amber: "#F59E0B",
  red: "#EF4444",
};

function verdictColor(verdict) {
  if (verdict === "FUND IT") return COLORS.green;
  if (verdict === "NEEDS WORK") return COLORS.amber;
  return COLORS.red;
}

function IconStar({ filled, size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth={1.75}>
      <path d="M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.9L12 17.8 5.8 21.1 7 14.2l-5-4.9 6.9-1L12 2z" />
    </svg>
  );
}

export default function LibraryPanel({ sessions, onClose, onSelect, onToggleStar }) {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const items = useMemo(() => {
    const withResults = sessions.filter((s) => s.result);
    let list = filter === "starred" ? withResults.filter((s) => s.starred) : withResults;

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((s) => {
        const parts = [
          s.idea,
          s.result?.tagline,
          s.result?.verdict,
          s.verdict,
          s.result?.bull_case,
          s.result?.bear_case,
          s.result?.killer_insight,
        ];
        return parts.some((p) => p && String(p).toLowerCase().includes(q));
      });
    }

    return list;
  }, [sessions, filter, search]);

  const starredCount = sessions.filter((s) => s.starred && s.result).length;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(8,12,24,0.9)",
        backdropFilter: "blur(12px)",
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0D1224",
          border: `1px solid ${COLORS.border}`,
          borderRadius: 20,
          width: "100%",
          maxWidth: 1000,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h2 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Idea Library</h2>
            <p style={{ color: COLORS.muted, fontSize: 14, margin: 0 }}>
              Every validated idea is saved here automatically. Star your favorites to find them quickly.
            </p>
          </div>
          <button type="button" onClick={onClose} style={{ background: "none", border: "none", color: COLORS.muted, fontSize: 24, cursor: "pointer" }}>×</button>
        </div>

        <div style={{ padding: "16px 24px 0", display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
          <input
            type="search"
            placeholder="Search ideas…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              flex: "1 1 220px",
              minWidth: 200,
              padding: "10px 14px",
              borderRadius: 10,
              border: `1px solid ${COLORS.border}`,
              background: "rgba(255,255,255,0.04)",
              color: COLORS.text,
              fontSize: 14,
              outline: "none",
            }}
          />
          {[
            { id: "all", label: `All (${sessions.filter((s) => s.result).length})` },
            { id: "starred", label: `Favorites (${starredCount})` },
          ].map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              style={{
                padding: "8px 14px",
                borderRadius: 99,
                border: `1px solid ${filter === f.id ? COLORS.indigo : COLORS.border}`,
                background: filter === f.id ? "rgba(99,102,241,0.15)" : "transparent",
                color: filter === f.id ? COLORS.text : COLORS.muted,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div style={{ padding: 24, overflowY: "auto", flex: 1 }}>
          {items.length === 0 ? (
            <p style={{ color: COLORS.muted, textAlign: "center", padding: "40px 0" }}>
              {search.trim()
                ? `No ideas match "${search.trim()}".`
                : filter === "starred"
                  ? "No favorites yet — star ideas you like from the results view."
                  : "No validated ideas yet. Run your first validation!"}
            </p>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
              {items.map((session) => {
                const color = verdictColor(session.result?.verdict || session.verdict);
                return (
                  <div
                    key={session.id}
                    style={{
                      background: COLORS.surface,
                      border: `1px solid ${session.starred ? "rgba(245,158,11,0.35)" : COLORS.border}`,
                      borderRadius: 14,
                      padding: 16,
                      cursor: "pointer",
                      position: "relative",
                      boxShadow: session.starred ? "0 0 24px rgba(245,158,11,0.08)" : "none",
                    }}
                    onClick={() => {
                      onSelect(session.id);
                      onClose();
                    }}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleStar(session.id);
                      }}
                      title={session.starred ? "Remove from favorites" : "Add to favorites"}
                      style={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        background: "none",
                        border: "none",
                        color: session.starred ? "#F59E0B" : COLORS.muted,
                        cursor: "pointer",
                        padding: 4,
                        display: "flex",
                      }}
                    >
                      <IconStar filled={session.starred} />
                    </button>

                    <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                      {session.result?.verdict || session.verdict || "—"}
                    </div>
                    <p style={{ fontSize: 14, fontWeight: 500, lineHeight: 1.45, marginBottom: 8, paddingRight: 28 }}>
                      {session.idea}
                    </p>
                    {session.result?.tagline && (
                      <p style={{ fontSize: 12, color: COLORS.muted, lineHeight: 1.5, marginBottom: 10 }}>{session.result.tagline}</p>
                    )}
                    <div style={{ fontSize: 11, color: COLORS.muted }}>
                      {new Date(session.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export { IconStar };
